import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  api,
  type SupportKind,
  type SupportTicketDetail,
  type SupportTicketRow,
} from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useEscClose } from "@/lib/escClose";
import { AdminList } from "./AdminList";
import { sendProblem, type UserTab } from "./supportLogic";
import {
  closeSupport,
  refreshSupportSummary,
  resetSupportStore,
  setSupportSummary,
  useSupportOpen,
  type SupportMode,
} from "./supportStore";
import { TicketPane } from "./TicketPane";
import { UserList } from "./UserList";
import "./support.css";

// Окно обращений на весь экран, поверх меню (эталон: .sp-pg). Одно окно на
// две роли: обычное — «Поддержка» (свои обращения), панель администратора —
// «Обращения» (все). Хозяин состояния здесь: какое обращение открыто, его
// переписка, опрос. Списки читают себя сами и перечитываются по `reloadKey`.

/** Как часто перечитывать открытое окно: ответ поддержки появляется без
 *  перезагрузки, а закрытая вкладка (hidden) сервер не нагружает. */
export const SUPPORT_WINDOW_POLL_MS = 15_000;

/** Монтируется в AppShell один раз; рисует окно, пока оно открыто. */
export function SupportHost() {
  const { state } = useAuth();
  const userId = state.status === "authed" ? state.user.id : null;
  const mode = useSupportOpen();
  // Окно и числа принадлежат вошедшему: вышел или вошёл другой — начинаем с нуля.
  useEffect(() => resetSupportStore, [userId]);
  if (!mode) return null;
  return <SupportWindow key={mode} mode={mode} />;
}

function statusOf(e: unknown): number | undefined {
  return e instanceof ApiError ? e.status : undefined;
}

function SupportWindow({ mode }: { mode: SupportMode }) {
  const admin = mode === "admin";
  const [reloadKey, setReloadKey] = useState(0);
  const bump = useCallback(() => setReloadKey((k) => k + 1), []);

  // --- свои обращения (только обычное окно) ---
  const [rows, setRows] = useState<SupportTicketRow[] | null>(null);
  const [listFailed, setListFailed] = useState(false);
  const [tab, setTab] = useState<UserTab>("open");

  // --- выбранное обращение ---
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<SupportTicketDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailFailed, setDetailFailed] = useState(false);
  const [closing, setClosing] = useState(false);
  // Ответы на устаревшие запросы (человек уже перешёл к другому обращению) отбрасываем.
  const seq = useRef(0);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selectedId;

  const root = useRef<HTMLDivElement>(null);
  useEscClose(closeSupport);
  useEffect(() => {
    root.current?.focus();
  }, []);

  // Список автора.
  useEffect(() => {
    if (admin) return;
    let live = true;
    api
      .supportTickets()
      .then((r) => {
        if (!live) return;
        setRows(r.items);
        setListFailed(false);
      })
      .catch(() => {
        if (live) setListFailed(true);
      });
    return () => {
      live = false;
    };
  }, [admin, reloadKey]);

  const loadDetail = useCallback(
    async (id: string, silent: boolean) => {
      const mine = ++seq.current;
      if (!silent) {
        setLoading(true);
        setDetailFailed(false);
      }
      try {
        const d = await (admin ? api.supportAdminTicket(id) : api.supportTicket(id));
        if (mine !== seq.current) return;
        setDetail(d);
        setDetailFailed(false);
        // Автор открыл обращение — его ответы прочитаны.
        if (!admin && d.unread > 0) {
          api
            .supportRead(id, d.unread)
            .then((s) => {
              setSupportSummary(s);
              bump();
            })
            .catch(() => undefined);
        }
      } catch {
        if (mine === seq.current && !silent) setDetailFailed(true);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    },
    [admin, bump],
  );

  function select(id: string) {
    setCreating(false);
    if (id === selectedId) return;
    setSelectedId(id);
    setDetail(null);
    void loadDetail(id, false);
  }

  // Опрос: пока окно открыто и вкладка видна, список и переписка живые.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      bump();
      void refreshSupportSummary();
      const id = selectedRef.current;
      if (id) void loadDetail(id, true);
    };
    const t = window.setInterval(tick, SUPPORT_WINDOW_POLL_MS);
    return () => window.clearInterval(t);
  }, [bump, loadDetail]);

  async function send(text: string, image?: File): Promise<string | null> {
    const id = selectedId;
    if (!id) return "Обращение не выбрано.";
    try {
      const d = await (admin ? api.supportAdminReply(id, text, image) : api.supportWrite(id, text, image));
      // Опрос, начатый до отправки, не должен затереть свежий ответ устаревшим.
      seq.current++;
      setLoading(false);
      if (selectedRef.current === id) setDetail(d);
      bump();
      void refreshSupportSummary();
      return null;
    } catch (e) {
      // Обращение закрыли, пока человек писал, — показать его закрытым.
      if (statusOf(e) === 409) void loadDetail(id, true);
      return sendProblem(statusOf(e));
    }
  }

  async function create(kind: SupportKind, title: string, text: string): Promise<string | null> {
    try {
      const d = await api.supportCreate({ kind, title, text });
      seq.current++;
      setCreating(false);
      setSelectedId(d.id);
      setDetail(d);
      setTab("open");
      bump();
      return null;
    } catch {
      return "Не удалось создать обращение. Попробуйте ещё раз.";
    }
  }

  async function closeTicket() {
    const id = selectedId;
    if (!id || closing) return;
    setClosing(true);
    try {
      const d = await api.supportAdminClose(id);
      seq.current++;
      setLoading(false);
      if (selectedRef.current === id) setDetail(d);
      bump();
      void refreshSupportSummary();
    } catch {
      /* кнопка вернётся; следующий опрос покажет, что на самом деле */
    } finally {
      setClosing(false);
    }
  }

  const thread = creating || !!selectedId;

  return (
    <div
      ref={root}
      className={"sup-win" + (thread ? " sup-win--thread" : "")}
      role="dialog"
      aria-modal="true"
      aria-label={admin ? "Обращения пользователей" : "Поддержка"}
      tabIndex={-1}
    >
      {admin ? (
        <AdminList selectedId={selectedId} reloadKey={reloadKey} onSelect={select} onExit={closeSupport} />
      ) : (
        <UserList
          rows={rows}
          failed={listFailed}
          tab={tab}
          onTab={setTab}
          selectedId={selectedId}
          creating={creating}
          onSelect={select}
          onNew={() => {
            seq.current++;
            setCreating(true);
            setSelectedId(null);
            setDetail(null);
          }}
          onRetry={bump}
          onExit={closeSupport}
        />
      )}
      <TicketPane
        mode={mode}
        creating={creating}
        detail={detail}
        loading={loading}
        failed={detailFailed}
        closing={closing}
        onBack={() => {
          seq.current++;
          setCreating(false);
          setSelectedId(null);
          setDetail(null);
        }}
        onExit={closeSupport}
        onCreate={create}
        onCancelNew={() => setCreating(false)}
        onSend={send}
        onCloseTicket={() => void closeTicket()}
      />
    </div>
  );
}
